import React, { useState } from 'react';
import {
  UploadCloud,
  PauseCircle,
  Info,
  ChevronDown,
  CheckCircle,
} from 'lucide-react';
import { AgentProps } from '../Interfaces/AgentProps';

const versionOptions = [
  { id: 5, label: 'v5 - Improved prompts' },
  { id: 4, label: 'v4 - Stable release' },
  { id: 3, label: 'v3 - Beta test' },
];
type Props = {
  agent?: AgentProps;
};

const AgentDeploymentPanel: React.FC<Props> = ({ agent }) => {
  const [selectedVersion, setSelectedVersion] = useState<number>(versionOptions[0].id);
  const [onHold, setOnHold] = useState(false);
  const [notes, setNotes] = useState('');
  const [published, setPublished] = useState(false);

  const handlePublish = () => {
    // Here you'd call your deployment API
    setPublished(true);
    setTimeout(() => setPublished(false), 3000);
  };

  return (
    <div className="p-6 text-white max-w-5xl mx-auto flex flex-col md:flex-row gap-8">
      {/* Left Panel: Form */}
      <div className="flex-1 space-y-6">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <UploadCloud className="w-5 h-5 text-indigo-500" />
          Deploy Agent Version
        </h2>

        {/* Version Selector */}
        <div>
          <label className="text-sm text-gray-400 block mb-1">Select Version</label>
          <div className="relative w-full">
            <select
              value={selectedVersion}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
              className="w-full bg-neutral-900 text-sm text-white px-3 py-2 rounded-md border border-neutral-800 appearance-none"
            >
              {versionOptions.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-3 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Deployment Notes */}
        <div>
          <label className="text-sm text-gray-400 block mb-1">Deployment Notes (optional)</label>
          <textarea
            rows={4}
            placeholder="e.g., Deploying new logic to production"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full bg-neutral-900 text-sm text-white px-3 py-2 rounded-md border border-neutral-800 resize-none"
          />
        </div>

        {/* Hold Toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={onHold}
            onChange={(e) => setOnHold(e.target.checked)}
            className="accent-indigo-600"
          />
          <label className="text-sm text-gray-300">Temporarily put agent on hold after deployment</label>
        </div>

        {/* Publish Button */}
        <button
          onClick={handlePublish}
          className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm px-4 py-2 rounded-md flex items-center gap-2"
        >
          <UploadCloud className="w-4 h-4" /> Publish Now
        </button>

        {/* Success message */}
        {published && (
          <div className="flex items-center gap-2 text-green-400 mt-4 text-sm">
            <CheckCircle className="w-4 h-4" /> Deployment successful!
          </div>
        )}
      </div>

      {/* Right Panel: Info */}
      <div className="w-full md:w-72 bg-neutral-900 border border-neutral-800 rounded-md p-4 text-xs text-gray-300 space-y-3">
        <div className="flex items-center gap-2 text-white text-sm font-medium">
          <Info className="w-4 h-4 text-indigo-400" />
          About Deployment
        </div>
        <p>
          Deploying a version will update the active agent logic and behavior in production.
          Use the notes field to document your changes.
        </p>

        <ul className="list-disc pl-4 space-y-1">
          <li>
            Versioning ensures traceability and rollback options.
          </li>
          <li>
            If “hold” is enabled, the agent will not respond until resumed.
          </li>
          <li>
            Once published, changes take effect immediately.
          </li>
        </ul>

        <p className="text-[11px] text-gray-500 pt-2">
          Note: It’s recommended to test each version thoroughly before publishing.
        </p>
      </div>
    </div>
  );
};

export default AgentDeploymentPanel;
